// ==============================================================================
// FILE: src/pages/ConsolidatedReportPage.tsx (COMPLETE & FIXED)
// ==============================================================================
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Button, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert, SelectChangeEvent,Autocomplete,TextField
} from '@mui/material';
import { Download as DownloadIcon, ArrowBack, InfoOutlined } from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import { ContractInfo, AvailablePeriod } from '../types';

export default function ConsolidatedReportPage(): JSX.Element {
  const { apiService, showNotification, setActivePage } = useAppContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [contracts, setContracts] = useState<ContractInfo[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<AvailablePeriod[]>([]);
  const [selectedContract, setSelectedContract] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.getContracts()
      .then(setContracts)
      .catch(err => {
        showNotification(err.message || 'Failed to load contracts.', 'error');
      });
  }, [apiService, showNotification]);

  useEffect(() => {
    if (selectedContract) {
      setSelectedMonth('');
      setSelectedYear('');
      setAvailablePeriods([]);

      apiService.getAvailablePeriodsForContract(selectedContract)
        .then(data => {
          setAvailablePeriods(data);
        })
        .catch(err => {
          showNotification(err.message || 'Failed to load available dates.', 'error');
        });
    }
  }, [selectedContract, apiService, showNotification]);

  const handleGenerateReport = async () => {
    if (!selectedContract || !selectedMonth || !selectedYear) {
      showNotification('Please select a contract, month, and year.', 'warning');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const blob = await apiService.downloadConsolidatedApprovalSheet(
        selectedContract,
        parseInt(selectedMonth),
        parseInt(selectedYear)
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const selectedContractData = contracts.find(c => c.id === selectedContract);
      const monthName = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'long' });
      a.download = `Approval_Sheet_${selectedContractData?.name || selectedContract}_${monthName}_${selectedYear}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate report.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isFormComplete = selectedContract && selectedMonth && selectedYear;
  
  const availableYears = availablePeriods.map(p => p.year);
  const availableMonths = selectedYear 
    ? availablePeriods.find(p => p.year === parseInt(selectedYear))?.months || [] 
    : [];

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => setActivePage('Claim History')}>
            Back to Claim History
            </Button>
        </Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Generate Consolidated Approval Sheet
      </Typography>
      
      <Alert severity="info" icon={<InfoOutlined fontSize="inherit" />} sx={{ mt: 2, mb: 3 }}>
        Please note: This report will only include data from claims with a "Completed" or "Partially Approved" status for the selected period.
      </Alert>
      
      <Paper variant="outlined" sx={{ p: 3, maxWidth: '800px' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>1. Select Contract</Typography>
            <Autocomplete
              fullWidth
              freeSolo
              options={contracts}
              getOptionLabel={(option: any) =>
                typeof option === 'string' ? option : `${option.name} (${option.id})`
              }
              value={contracts.find(c => c.id === selectedContract) || null}
              onChange={(event: any, newValue: any) => {
                if (typeof newValue === 'object' && newValue) {
                  setSelectedContract(newValue.id);
                } else if (!newValue) {
                  setSelectedContract('');
                }
              }}
              renderInput={(params: any) =>
                <TextField {...params} label="Contract" placeholder="Search by name or ID..." />
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>2. Select Period</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={!selectedContract || availableYears.length === 0}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e: SelectChangeEvent) => {
                  setSelectedYear(e.target.value);
                  setSelectedMonth('');
                }}
              >
                {availableYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={!selectedYear || availableMonths.length === 0}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e: SelectChangeEvent) => setSelectedMonth(e.target.value)}
              >
                {availableMonths.map((month: number) => (
                  <MenuItem key={month} value={month}>
                    {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerateReport}
              disabled={loading || !isFormComplete}
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
              sx={{ minWidth: '300px' }}
            >
              {loading ? 'Generating...' : 'Generate & Download'}
            </Button>
          </Grid>
          {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
        </Grid>
      </Paper>
    </Paper>
  );
}